import ReactECharts from 'echarts-for-react';
import industryProblems from '../../data/industryProblems.json';
import { useIsDark } from '../../useIsDark';

/**
 * MemoryDecayChart — long-term memory recall across sustained interactions
 * (blog14, Fig 3).
 *
 * Chart-type-first: two-point degradation → ECharts vertical bar. Data reads
 * from industryProblems.json (figures.memory_decay). Brand colors: early =
 * green (full-auto/trusted), sustained = red (manual/unreliable). Category
 * labels wrap to a second row (no overlap). Theme: `dark` prop override,
 * otherwise follows the live html class theme (Rule 29). ONE shared formatter.
 */

const BRAND = {
  success: '#10B981',
  error: '#EF4444',
};

type DecayItem = { label: string; value: number };

const formatPct = (v: number) => `${v.toFixed(0)}%`;

export const MemoryDecayChart = ({
  height = 320,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const fig = (
    industryProblems as unknown as {
      figures: { memory_decay: { source: string; items: DecayItem[] } };
    }
  ).figures.memory_decay;

  const C = isDark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params.map((p) => `${p.marker} ${p.name}: <b>${formatPct(p.value)}</b>`).join('<br/>'),
    },
    grid: { left: 52, right: 48, top: 40, bottom: 72 },
    xAxis: {
      type: 'category',
      data: fig.items.map((i) => i.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisLabel: {
        color: C.axis,
        fontSize: 12,
        fontWeight: 700,
        interval: 0,
        width: 110,
        overflow: 'break',
        lineHeight: 15,
      },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'recall accuracy (baseline)',
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
    },
    series: [
      {
        name: 'Recall accuracy',
        type: 'bar',
        barWidth: '36%',
        data: fig.items.map((i) => ({
          value: i.value,
          itemStyle: {
            color: i.label.toLowerCase().startsWith('early') ? BRAND.success : BRAND.error,
            borderRadius: [6, 6, 0, 0],
          },
        })),
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontWeight: 700,
          fontSize: 13,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center`}>
        Figure 3. Long-term memory recall across sustained interactions ({fig.source}): a ~30% accuracy
        drop at the model level — and no shared brain across departments unless one is built in.
      </p>
    </div>
  );
};
